<template>
  <div 
    class="dashboard-table-element"
    :class="{ 
      'lock': elementInfo.lock, 
      'invisible': elementInfo.isInvisible 
    }"
    :data-element-id="elementInfo.id"
    :style="{
      width: elementInfo.width + 'px',
      height: elementInfo.height + 'px',
      left: elementInfo.left + 'px',
      top: elementInfo.top + 'px',
    }"
  >
    <div
      class="rotate-wrapper"
      :style="{ transform: `rotate(${elementInfo.rotate}deg)` }"
    >
      <div 
        class="element-content"
        ref="elementRef"
        :style="{
          width: elementInfo.width + 'px',
          height: elementInfo.height + 'px',
        }"
        v-contextmenu="contextmenus || (() => null)"
        @mousedown="$event => handleSelectElement($event)"
        @touchstart="$event => handleSelectElement($event)"
        @dragstart.prevent
        @selectstart.prevent
      >
        <ElementOutline
          :width="elementInfo.width"
          :height="elementInfo.height"
          :outline="elementInfo.outline"
        />
        <!-- Use live DashboardTable with snapshot data -->
        <DashboardTable 
          v-if="elementInfo.type === 'dashboard-table'" 
          overflow="auto"
          :container-width="elementInfo.width"
          :container-height="elementInfo.height"
          :snapshot="(elementInfo as any)._snapshot"
          :is-template-placeholder="elementInfo.isTemplatePlaceholder"
          :attritional-only="elementInfo.attritionalOnly"
          :large-only="elementInfo.largeOnly"
          :weather-only="elementInfo.weatherOnly"
          :total-ultimate-only="elementInfo.totalUltimateOnly"
          :loss-ratios-only="elementInfo.lossRatiosOnly"
          :attritional-large-expanded="elementInfo.attritionalLargeExpanded"
          :large-loss-load="elementInfo.largeLossLoad"
          :max-rows="elementInfo.maxRows"
        />
        
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { type PPTElement } from '@/types/slides'
import type { ContextmenuItem } from '@/components/Contextmenu/types'
import DashboardTable from '@/components/DashboardTable/DashboardTable.vue'
import ElementOutline from '@/views/components/element/ElementOutline.vue'
import { useDashboardStore } from '@/store/dashboard'
import { usePortfolioStore } from '@/store/portfolio'
import { portfolioService } from '@/services/portfolioService'

const props = defineProps<{
  elementInfo: PPTElement & {
    width: number
    height: number
    rotate: number
    outline?: any
    props?: any
    // Dashboard table specific properties
    portfolioId?: string
    portfolioName?: string
    bounceId?: string
    bounceName?: string
    selectedFilters?: any
    accidentUnderwriting?: 'uw' | 'acc'
    columnConfig?: any
    isTemplatePlaceholder?: boolean
    isInvisible?: boolean
    attritionalOnly?: boolean
    largeOnly?: boolean
    weatherOnly?: boolean
    totalUltimateOnly?: boolean
    lossRatiosOnly?: boolean
    attritionalLargeExpanded?: boolean
    largeLossLoad?: boolean
    maxRows?: number
    claimsTypeSelection?: boolean[]
    _snapshot?: any
  }
  selectElement?: (e: MouseEvent | TouchEvent, element: PPTElement, canMove?: boolean) => void
  contextmenus?: () => ContextmenuItem[] | null
}>()

const elementRef = ref<HTMLElement>()
const dashboardStore = useDashboardStore()
const portfolioStore = usePortfolioStore()


onMounted(async () => {
    if ((props.elementInfo as any)._snapshot) {
    const snapshot = (props.elementInfo as any)._snapshot
    const deepCopy = (obj: any) => JSON.parse(JSON.stringify(obj))
    
    // Restore dashboard state from snapshot
    dashboardStore.dashboards = deepCopy(snapshot.dashboards)
    
    const claimsNatureLen = Array.isArray(snapshot.portfolioParameters?.claims_nature)
      ? snapshot.portfolioParameters.claims_nature.length
      : 0
    const expectedNormalise = Math.max(0, claimsNatureLen - 1)
    const padNormalise = (raw: boolean[] | undefined) => {
      const next = Array.isArray(raw) ? [...raw] : []
      while (next.length < expectedNormalise) next.push(true)
      return expectedNormalise > 0 ? next.slice(0, expectedNormalise) : next
    }

    if (props.elementInfo.claimsTypeSelection) {
      portfolioStore.normaliseSelection = padNormalise(props.elementInfo.claimsTypeSelection)
    }
    else if (snapshot.normaliseSelection) {
      portfolioStore.normaliseSelection = padNormalise(deepCopy(snapshot.normaliseSelection))
    }
    dashboardStore.dashboard_data = deepCopy(snapshot.dashboard_data)
    dashboardStore.dashboard_data_column = deepCopy(snapshot.dashboard_data_column)
    dashboardStore.seasonality_parameters = deepCopy(snapshot.seasonality_parameters)
    dashboardStore.totalData = deepCopy(snapshot.totalData)
    dashboardStore.quarterly_dashboard_data = deepCopy(snapshot.quarterly_dashboard_data)
    dashboardStore.binder_dashboard_data = deepCopy(snapshot.binder_dashboard_data)
    dashboardStore.yearly_dashboard_data = deepCopy(snapshot.yearly_dashboard_data)
    dashboardStore.chart_data = deepCopy(snapshot.chart_data)
    dashboardStore.isQuarterSubTotal = snapshot.isQuarterSubTotal
    dashboardStore.isQuarterSubTotalUp = snapshot.isQuarterSubTotalUp
    dashboardStore.isBinderSubTotal = snapshot.isBinderSubTotal
    dashboardStore.isBinderSubTotalUp = snapshot.isBinderSubTotalUp
    dashboardStore.isYearSubTotal = snapshot.isYearSubTotal
    dashboardStore.isYearSubTotalUp = snapshot.isYearSubTotalUp
    dashboardStore.showColumn = deepCopy(snapshot.showColumn)
    dashboardStore.margin = deepCopy(snapshot.margin)
    dashboardStore.showColumnTotal = snapshot.showColumnTotal
    dashboardStore.totalMargin = snapshot.totalMargin
    dashboardStore.isShowingExposure = snapshot.isShowingExposure
    dashboardStore.underwriting_loss_ratios = snapshot.underwriting_loss_ratios
    dashboardStore.visibleColumns = deepCopy(snapshot.visibleColumns)
    dashboardStore.isBindedYears = snapshot.isBindedYears
    
    if (snapshot.data_CommissionColumns !== undefined) {
      dashboardStore.data_CommissionColumns = deepCopy(snapshot.data_CommissionColumns)
    }
    
    if (snapshot.portfolioParameters) {
      portfolioStore.parameters = deepCopy(snapshot.portfolioParameters)
    }
    
    return
  }
  
  if (!props.elementInfo.portfolioId || !props.elementInfo.bounceId || props.elementInfo.isTemplatePlaceholder) {
    return
  }
  
  try {      
    // Fetch complete portfolio data
    const completeData = await portfolioService.fetchCompletePortfolioData(
      props.elementInfo.portfolioId,
      props.elementInfo.bounceId
    )
    
    // Parse bounce name
    const bounceName = props.elementInfo.bounceName || ''
    completeData.portfolioName = props.elementInfo.portfolioName || ''
    completeData.bounceName = bounceName.slice(26) || bounceName.slice(25)
    completeData.bounceDate = bounceName.slice(0, 6)
    completeData.bounceFullName = bounceName
    
    delete completeData.normaliseSelection
    completeData.normaliseSelection = undefined
    
    // Set portfolio data in dashboard store
    dashboardStore.setPortfolioData(completeData)
    
    // Apply claims type selection if stored in element
    if (props.elementInfo.claimsTypeSelection && props.elementInfo.claimsTypeSelection.length > 0) {
      portfolioStore.normaliseSelection = props.elementInfo.claimsTypeSelection
    }
    
    // Load dashboard data
    await dashboardStore.loadDashboard(completeData)
    
    // Wait for portfolio data to be fully processed
    await new Promise(resolve => setTimeout(resolve, 200))
    
    // Apply stored filters
    if (props.elementInfo.selectedFilters) {
      Object.assign(dashboardStore.selectedFilters, props.elementInfo.selectedFilters)
    }
    
    if (props.elementInfo.accidentUnderwriting) {
      dashboardStore.setAccidentUnderwriting(props.elementInfo.accidentUnderwriting)
    }
    
    if (props.elementInfo.columnConfig?.showColumn) {
      dashboardStore.setColumnState({
        showColumn: props.elementInfo.columnConfig.showColumn,
        margin: props.elementInfo.columnConfig.margin,
        showColumnTotal: props.elementInfo.columnConfig.showColumnTotal,
        totalMargin: props.elementInfo.columnConfig.totalMargin
      })
    }
    
    await dashboardStore.loadDashboard(completeData)
      
  }
  catch (error) {
    console.error('❌ Error loading portfolio data for dashboard table:', error)
  }
})

const handleSelectElement = (e: MouseEvent | TouchEvent, canMove = false) => {
  if (props.elementInfo.lock) return
  e.stopPropagation()
  e.preventDefault()

  // Disable resizing for template dashboard tables
  if (props.selectElement) {
    props.selectElement(e, props.elementInfo, canMove)
  }
}
</script>

<style scoped>
.dashboard-table-element {
  position: absolute;
  cursor: pointer;
  user-select: none;
  pointer-events: none; /* Disable all pointer events */
}

.dashboard-table-element .element-content {
  pointer-events: auto; /* Re-enable for selection only */
}

.rotate-wrapper {
  width: 100%;
  height: 100%;
}

.element-content {
  position: relative;
  width: 100%;
  height: 100%;
}

/* Drag handlers removed for template dashboard tables */

.lock,
.lock .element-content {
  cursor: default;
}

.invisible {
  opacity: 0;
  pointer-events: auto; /* Keep clickable for portfolio data functionality */
}
</style>
